import { useState } from "react";
import { CheckCircle2, IdCard, Loader2, ShieldX } from "lucide-react";
import { ConfirmDialog } from "@components/ui/ConfirmDialog";
import { VerifastIframePopup } from "@components/verifast/VerifastIframePopup";
import { APPLICANT_CONTENT } from "@/features/applicant/constants/applicantContent";
import { trackStartIdentityVerification } from "@/lib/utils/analytics";
import { applicantConfirmDialogProps } from "@/features/applicant/constants/applicantDialogProps";
import { GovernmentIdUploadSection } from "@/features/applicant/components/GovernmentIdUploadSection";
import type { useVerifastIdentity } from "@/features/applicant/hooks/useVerifastIdentity";
import type { useGovernmentIdUpload } from "@/features/applicant/hooks/useGovernmentIdUpload";

type VerifastIdentity = ReturnType<typeof useVerifastIdentity>;
type GovernmentIdUploadState = ReturnType<typeof useGovernmentIdUpload>;

interface IdVerificationSectionProps {
  verifast: VerifastIdentity;
  govIdUpload: GovernmentIdUploadState;
  showDocumentUploads: boolean;
  onUseDocumentUploads: () => void;
  isReadOnly?: boolean;
}

function verifastOutputMessage(verifast: VerifastIdentity): string {
  if (verifast.status?.error_message) {
    return verifast.status.error_message;
  }
  return "Verifast was unable to verify this identity.";
}

export function IdVerificationSection({
  verifast,
  govIdUpload,
  showDocumentUploads,
  onUseDocumentUploads,
  isReadOnly = false,
}: IdVerificationSectionProps) {
  const [showStartConfirm, setShowStartConfirm] = useState(false);

  const copy = APPLICANT_CONTENT.application.sections.idVerification;
  const isDocumentOnlyMode =
    !verifast.enabled &&
    !verifast.configLoading &&
    !verifast.hasStoredSession;

  const closeStartConfirm = () => setShowStartConfirm(false);

  const handleUseDocumentUploads = () => {
    closeStartConfirm();
    onUseDocumentUploads();
  };

  return (
    <>
      <div
        data-testid="about-id-verification-section"
        className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6"
      >
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <IdCard className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">{copy.title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isDocumentOnlyMode || showDocumentUploads
                ? copy.documentUploadSubtitle
                : copy.subtitle}
            </p>
          </div>
        </div>

        {verifast.configLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading verification status...
          </div>
        )}

        {isDocumentOnlyMode && !verifast.configLoading && (
          <GovernmentIdUploadSection
            gov={govIdUpload}
            isReadOnly={isReadOnly}
            testIdPrefix="about-gov-id"
            embedded
          />
        )}

        {!isDocumentOnlyMode && !verifast.configLoading && verifast.isComplete && (
          <div
            data-testid="about-id-verification-success"
            className="rounded-lg border border-green-200 bg-green-50 p-4"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">{copy.successTitle}</p>
                <p className="mt-1 text-sm text-green-700">{copy.successBody}</p>
              </div>
            </div>
          </div>
        )}

        {!isDocumentOnlyMode &&
          !verifast.configLoading &&
          verifast.isInProgress &&
          !verifast.enabled && (
          <div
            data-testid="about-id-verification-in-progress-readonly"
            className="rounded-lg border border-amber-200 bg-amber-50 p-4"
          >
            <p className="font-semibold text-amber-800">{copy.reviewTitle}</p>
            <p className="mt-1 text-sm text-amber-700">
              Your identity verification is in progress.
            </p>
          </div>
        )}

        {!isDocumentOnlyMode &&
          !verifast.configLoading &&
          verifast.isNeedsReview &&
          !showDocumentUploads && (
          <div
            data-testid="about-id-verification-review"
            className="rounded-lg border border-amber-200 bg-amber-50 p-4"
          >
            <p className="font-semibold text-amber-800">{copy.reviewTitle}</p>
            <p className="mt-1 text-sm text-amber-700">{copy.reviewBody}</p>
          </div>
        )}

        {!isDocumentOnlyMode &&
          !verifast.configLoading &&
          verifast.isFailed && (
          <div
            data-testid="about-id-verification-unsuccessful"
            className="rounded-lg border border-red-200 bg-red-50 p-4"
          >
            <div className="flex items-start gap-3">
              <ShieldX className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              <div className="space-y-2 text-sm text-red-800">
                <p className="text-base font-semibold">{copy.unsuccessfulTitle}</p>
                <p>{verifastOutputMessage(verifast)}</p>
                <p>
                  {copy.unsuccessfulBody}{" "}
                  <a
                    href={`tel:${copy.supportPhoneTel}`}
                    className="font-medium underline"
                  >
                    {copy.supportPhone}
                  </a>{" "}
                  or email{" "}
                  <a
                    href={`mailto:${copy.supportEmail}`}
                    className="font-medium underline"
                  >
                    {copy.supportEmail}
                  </a>{" "}
                  with any questions.
                </p>
                <p>{copy.failedUploadHint}</p>
              </div>
            </div>
          </div>
        )}

        {!isDocumentOnlyMode &&
          verifast.canUseVerifast &&
          !verifast.configLoading &&
          !verifast.isComplete &&
          !showDocumentUploads &&
          !verifast.isFailed &&
          !verifast.isNeedsReview && (
          <div>
            {!isReadOnly && (
              <button
                type="button"
                data-testid="about-id-verification-start-button"
                onClick={() => setShowStartConfirm(true)}
                disabled={verifast.starting || !verifast.canStart}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1597ff] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {verifast.starting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {copy.preparing}
                  </>
                ) : verifast.isInProgress ? (
                  copy.continueButton
                ) : (
                  copy.startButton
                )}
              </button>
            )}
          </div>
        )}

        {!isDocumentOnlyMode && showDocumentUploads && !verifast.isComplete && (
          <GovernmentIdUploadSection
            gov={govIdUpload}
            isReadOnly={isReadOnly}
            testIdPrefix="about-gov-id"
            embedded
          />
        )}
      </div>

      {!isDocumentOnlyMode && (
      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={showStartConfirm}
        title={copy.confirmTitle}
        message={copy.confirmMessage}
        primaryLabel={copy.confirmOk}
        disabled={verifast.starting}
        onPrimary={() => {
          closeStartConfirm();
          trackStartIdentityVerification();
          void verifast.startVerification();
        }}
        secondaryLabel={copy.confirmCancel}
        onSecondary={closeStartConfirm}
        onDismiss={closeStartConfirm}
        overlayDataTestId="about-id-verification-confirm-modal"
        primaryButtonDataTestId="about-id-verification-confirm-ok-button"
        secondaryButtonDataTestId="about-id-verification-confirm-cancel-button"
        closeButtonDataTestId="about-id-verification-confirm-close-button"
        belowFooter={
          !isReadOnly ? (
            <button
              type="button"
              data-testid="about-id-verification-use-document-uploads-link"
              onClick={handleUseDocumentUploads}
              className="text-sm font-medium text-[#1597ff] underline hover:text-[#0d7acc]"
            >
              {copy.useDocumentUploadsLink}
            </button>
          ) : null
        }
      />
      )}

      {!isDocumentOnlyMode && (
      <VerifastIframePopup
        isOpen={verifast.showPopup}
        iframeUrl={verifast.iframeUrl}
        verifastOrigin={verifast.config?.VERIFAST_APPLICANT_HOST}
        isLoading={verifast.starting}
        onStarted={() => {
          verifast.setStatus((prev) => ({
            ...(prev || {}),
            id_verify_status: "in_progress",
          }));
        }}
        onStepComplete={verifast.handleStepComplete}
        onClose={verifast.handlePopupClose}
      />
      )}
    </>
  );
}
